import type { WritableUtmParameters } from '@shared/utils/utmParameters';
import { PRIORITIZE_TAG_PARAM } from '@shared/constants/accommodations';

export const JOY_HOTEL_DISCOUNT_CODE_PARAM = 'joyHotelDiscountCode';
// Declared locally, like the discount param above, rather than imported from
// `JoyHotelQueryStringParameters` — that enum lives beside a provider component, and this module is
// pulled into places that must not drag one in. Every name here is a contract with checkout either way.
export const JOY_REFERRAL_REWARD_CODE_PARAM = 'joyReferralRewardCode';
export const JOY_REFERRAL_CODE_PARAM = 'joyReferralCode';

/**
 * Room occupancy input type that accepts both strict number arrays
 * and GraphQL InputMaybe arrays (which may contain null/undefined).
 */
interface RoomOccupancyInput
  extends Readonly<{
    adults: number;
    childrenAges?: ReadonlyArray<number | null | undefined> | null;
  }> {}

/**
 * Formats a room's occupancy into the URL parameter format.
 * @example
 * // returns "a2:c5:c7" for a room with 2 adults and 2 children aged 5 and 7
 * formatRoomOccupancyToString({ adults: 2, childrenAges: [5, 7] })
 */
export const formatRoomOccupancyToString = (room: RoomOccupancyInput): string => {
  const adultsParam = `a${room.adults}`;
  const validChildrenAges = room.childrenAges?.filter((age): age is number => age != null);
  const childrenParam = validChildrenAges?.length ? `:c${validChildrenAges.join(':c')}` : '';
  return `${adultsParam}${childrenParam}`;
};

/**
 * Formats multiple room occupancies into a comma-separated URL parameter string.
 * @example
 * // returns "a2:c5:c7,a3" for 2 rooms: first with 2 adults and 2 children, second with 3 adults
 * formatRoomOccupanciesToString([{ adults: 2, childrenAges: [5, 7] }, { adults: 3 }])
 */
export const formatRoomOccupanciesToString = (rooms: ReadonlyArray<RoomOccupancyInput>): string => {
  return rooms.map(formatRoomOccupancyToString).join(',');
};

interface ConvertToJoyNativeCheckoutUrlParams
  extends Readonly<{
    url: string;
    currency: string;
    checkIn?: string;
    checkOut?: string;
    roomsString?: string;
    discountCode?: string | null;
    /**
     * Share Your Stay reward coupon. Needed here because this URL skips the hotel page and lands
     * straight on checkout, so it misses the forwarding every other route picks up on the way.
     */
    referralRewardCode?: string | null;
    /**
     * Share Your Stay *recipient* code — the one a sharer's link puts on the guest's URL. Needed for
     * the same reason as the reward code above, and it is the code checkout reads when it creates the
     * reservation: dropped here, the recipient's discount and the sharer's reward are both lost.
     */
    referralCode?: string | null;
    /**
     * Every slot optional: the writer below sets only truthy values, and a caller may legitimately
     * hold a `utm_content` arm without a complete triplet — an inbound `utm_medium` outside
     * `UTM_MEDIUMS` is dropped on read, which used to take the arm down with it.
     */
    utmParams?: WritableUtmParameters;
    /**
     * The search's curated-tag hint, comma-joined (`honeymoon`). Carried so the property page's
     * "modify search" link can return the guest to the same honeymoon results.
     */
    prioritizeTag?: string;
  }> {}

export const convertToJoyNativeCheckoutUrl = ({
  url,
  checkIn,
  checkOut,
  roomsString,
  discountCode,
  referralRewardCode,
  referralCode,
  currency,
  utmParams,
  prioritizeTag,
}: ConvertToJoyNativeCheckoutUrlParams): URL => {
  const redirectUrl = new URL(url);

  const urlParams: Readonly<Record<string, string | undefined>> = {
    checkIn,
    checkOut,
    rooms: roomsString,
  };

  ['checkIn', 'checkOut', 'rooms'].forEach(param => {
    const value = urlParams[param];
    if (value) {
      redirectUrl.searchParams.set(param, value);
    }
  });

  if (discountCode) {
    redirectUrl.searchParams.set(JOY_HOTEL_DISCOUNT_CODE_PARAM, discountCode);
  }
  if (referralRewardCode) {
    redirectUrl.searchParams.set(JOY_REFERRAL_REWARD_CODE_PARAM, referralRewardCode);
  }
  if (referralCode) {
    redirectUrl.searchParams.set(JOY_REFERRAL_CODE_PARAM, referralCode);
  }
  if (prioritizeTag) {
    redirectUrl.searchParams.set(PRIORITIZE_TAG_PARAM, prioritizeTag);
  }
  redirectUrl.searchParams.set('currency', currency);

  // Add UTM parameters if provided
  if (utmParams) {
    const utmFields = ['utm_medium', 'utm_source', 'utm_campaign', 'utm_source_secondary', 'utm_content'] as const;
    utmFields.forEach(field => {
      const value = utmParams[field];
      if (value) {
        redirectUrl.searchParams.set(field, value);
      }
    });
  }

  return redirectUrl;
};
